import { RouteObject } from "react-router";

import AuthGuard from "@/middleware/AuthGuard";
import { DynamicLayout } from "../layouts/DynamicLayout";
import { PermissionGuard } from "@/components/shared/PermissionGuard";
import { DefaultRedirect } from "@/components/shared/DefaultRedirect";
import { PERMISSIONS } from "@/constants/permissions";

/**
 * Protected routes configuration
 * These routes require authentication to access
 * Uses AuthGuard middleware to verify user authentication
 */
const protectedRoutes: RouteObject = {
  id: "protected",
  Component: AuthGuard,
  children: [
    // The dynamic layout supports both the main layout and the sideblock.
    {
      Component: DynamicLayout,
      children: [
        {
          index: true,
          element: <DefaultRedirect />,
        },
        {
          path: "dashboards",
          children: [
            {
              index: true,
              element: <DefaultRedirect />,
            },
            {
              path: "home",
              lazy: async () => ({
                Component: (await import("@/app/pages/dashboards/home"))
                  .default,
              }),
            },
            {
              path: "coming-soon",
              lazy: async () => ({
                Component: (await import("@/app/pages/dashboards/coming-soon"))
                  .default,
              }),
            },
            {
              path: "chats",
              lazy: async () => {
                const ChatPage = (await import("@/app/pages/dashboards/chat"))
                  .default;
                return {
                  Component: () => (
                    <PermissionGuard permissions={[PERMISSIONS.ACTIVE_CHAT]}>
                      <ChatPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "ticket-list",
              lazy: async () => {
                const TicketingPage = (
                  await import("@/app/pages/supervisor/ticketingManagement")
                ).default;
                return {
                  Component: () => (
                    <PermissionGuard
                      permissions={[PERMISSIONS.AGENT_TICKET_LIST]}
                    >
                      <TicketingPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "history",
              lazy: async () => {
                const HistoryPage = (
                  await import("@/app/pages/dashboards/history")
                ).default;
                return {
                  Component: () => (
                    <PermissionGuard permissions={[PERMISSIONS.PREVIOUS_CHAT]}>
                      <HistoryPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "agents",
              lazy: async () => {
                const AgentsPage = (
                  await import("@/app/pages/supervisor/agents")
                ).default;
                return {
                  Component: () => (
                    <PermissionGuard permissions={[PERMISSIONS.AGENT_LIST]}>
                      <AgentsPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "history-list",
              lazy: async () => {
                const HistoryPage = (
                  await import("@/app/pages/supervisor/history")
                ).default;
                return {
                  Component: () => (
                    <PermissionGuard permissions={[PERMISSIONS.HISTORY_LIST]}>
                      <HistoryPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "queues",
              lazy: async () => {
                const QueuesPage = (
                  await import("@/app/pages/supervisor/queues")
                ).default;
                return {
                  Component: () => (
                    <PermissionGuard permissions={[PERMISSIONS.QUEUE_LIST]}>
                      <QueuesPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "livechat",
              lazy: async () => {
                const LiveChatPage = (
                  await import("@/app/pages/supervisor/liveChat")
                ).default;
                return {
                  Component: () => (
                    <PermissionGuard permissions={[PERMISSIONS.AGENT_LIST]}>
                      <LiveChatPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "ticketing",
              lazy: async () => {
                const TicketingPage = (
                  await import("@/app/pages/supervisor/ticketingManagement")
                ).default;
                return {
                  Component: () => (
                    <PermissionGuard permissions={[PERMISSIONS.TICKET_MANAGEMENT, PERMISSIONS.TECH_SUPPORT_TICKET_LIST]}>
                      <TicketingPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "admin_queues",
              lazy: async () => {
                const QueuesPage = (await import("@/app/pages/admin/queue"))
                  .default;
                return {
                  Component: () => (
                    <PermissionGuard
                      permissions={[PERMISSIONS.ADMIN_QUEUE_LIST]}
                    >
                      <QueuesPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "admin_users",
              lazy: async () => {
                const UsersPage = (await import("@/app/pages/admin/users"))
                  .default;
                return {
                  Component: () => (
                    <PermissionGuard
                      permissions={[PERMISSIONS.ADMIN_QUEUE_LIST]}
                    >
                      <UsersPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "settings",
              lazy: async () => ({
                Component: (
                  await import("@/app/pages/dashboards/settings/resetPassword")
                ).default,
              }),
            },
            {
              path: "web-scraping",
              lazy: async () => {
                const WebScrapingPage = (
                  await import("@/app/pages/dashboards/webScraping")
                ).default;
                return {
                  Component: () => (
                    <PermissionGuard permissions={[PERMISSIONS.KNOWLEDGE_BASE]}>
                      <WebScrapingPage />
                    </PermissionGuard>
                  ),
                };
              },
            },
            {
              path: "reports",
              children: [
                {
                  path: "conversations",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/conversation&Users")
                    ).default,
                  }),
                },
                {
                  path: "conversations/:userId",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/conversation&Users/UserChatsPage")
                    ).default,
                  }),
                },
                {
                  path: "sentiment-conversations/:conversationId",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/conversation&Users/ConversationChatsPage")
                    ).default,
                  }),
                },
                {
                  path: "conversations/:userId",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/conversation&Users/UserChatsPage")
                    ).default,
                  }),
                },
                {
                  path: "sentiment-conversations/:conversationId",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/conversation&Users/ConversationChatsPage")
                    ).default,
                  }),
                },
                {
                  path: "module-analytics",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/moduleAnalytics")
                    ).default,
                  }),
                },
                {
                  path: "performance-metrics",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/performanceMetrics")
                    ).default,
                  }),
                },
                {
                  path: "live-agent-analytics",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/liveAgentAnalytics")
                    ).default,
                  }),
                },
                {
                  path: "live-agent-analytics/:status",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/liveAgentAnalytics/components/AgentDetailsPage")
                    ).default,
                  }),
                },
                {
                  path: "feedback-analytics",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/feedbackAnalytics")
                    ).default,
                  }),
                },
                {
                  path: "peak-usage",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/peakUsage")
                    ).default,
                  }),
                },
                {
                  path: "sentiment-analytics",
                  lazy: async () => ({
                    Component: (
                      await import("@/app/pages/dashboards/reports&Analytics/sentimentAnalytics")
                    ).default,
                  }),
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

export { protectedRoutes };
